<template>
  <div>
    <Header ref="header">
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="$router.back()" />
      </template>
      <template v-if="application">
        {{ getFormattedWorkTimeRange }}
      </template>
    </Header>
    <div
      v-if="
        application &&
          application.order &&
          application.attendanceCorrection &&
          application.attendanceCorrection.status === 'rejected'
      "
      class="card bg-primary-bg border-primary-yellow mx-3 mt-3"
    >
      <div class="card-body p-2">
        勤怠変更申請が却下されました。至急内容をご確認のうえ再申請してください
      </div>
    </div>
    <ApplicationCard
      v-if="application"
      :application="application"
      class="mt-3 mx-3 mb-2"
    />
    <ApplicationsEvaluation
      v-if="application && evaluation"
      :officeName="application.order.office.name"
      :evaluation="evaluation"
    />
    <ApplicationsMenu
      v-if="application"
      :application="application"
      :favoriteOffice="favoriteOffice"
      :isSentMessage="isSentMessage"
      @addFavoriteOffice="addFavoriteOffice"
    />
    <AppFooter />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import ApplicationsEvaluation from "@/components/organisms/applications/ApplicationsEvaluation.vue";
import ApplicationsMenu from "@/components/organisms/applications/ApplicationsMenu.vue";
import Header from "@/components/organisms/common/Header.vue";
import ApplicationCard from "@/components/organisms/applications/ApplicationCard.vue";
import { messageService } from "@/app/services/MessageService";
import { applicationService } from "@/app/services/ApplicationService";
import { favoriteService } from "@/app/services/FavoriteService";
import { orderService } from "@/app/services/OrderService";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { authStore } from "@/store/auth";
import { Application } from "@/types/order/Application";
import { EvaluationByOffice } from "@/types/order/EvaluationByOffice";
import { FavoriteOffice } from "@/types/office/FavoriteOffice";
import { inAppReviewService } from "@/app/services/InAppReviewService";

@Component({
  components: {
    FeatherIcon,
    AppFooter,
    ApplicationsEvaluation,
    ApplicationsMenu,
    Header,
    ApplicationCard
  }
})
export default class ApplicationsDetail extends Vue {
  application: Application | null = null;
  evaluation: EvaluationByOffice | null = null;
  isSentMessage = false;
  favoriteOffice: FavoriteOffice | null = null;

  get getFormattedWorkTimeRange() {
    return (
      orderService.getFormattedDateWithSlash(this.application!.order) +
      " " +
      applicationService.getFormattedWorkTimeRange(this.application!)
    );
  }

  get applicationId() {
    return Number(this.$route.params.applicationId);
  }

  get officeId() {
    return this.application?.order.office.id;
  }

  async created() {
    try {
      this.$loading.show();
      await this.fetchApplication();
      await Promise.all([
        this.fetchEvaluationByOffice(),
        this.fetchMessage(),
        this.fetchFavoriteOffice()
      ]);
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchFavoriteOffice() {
    const {
      results: favoriteOffices
    } = await favoriteService.fetchFavoriteOffices({
      office: this.officeId!,
      limit: 1
    });
    if (favoriteOffices.length) {
      this.favoriteOffice = favoriteOffices[0];
    }
  }

  async addFavoriteOffice(id: number) {
    this.$loading.show();
    try {
      const body = {
        office: this.officeId!,
        isFavorite: true
      };
      id
        ? await favoriteService.updateFavoriteOffices(id, body)
        : await favoriteService.createFavoriteOffices(body);
      // refetch する
      await this.fetchFavoriteOffice();
      await this.$bvModal.msgBoxOk("お気に入り事業所に登録しました", {
        centered: true
      });
      await inAppReviewService.launchInAppReview("FAVORITE_OFFICE");
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchEvaluationByOffice() {
    const { results } = await orderService.fetchEvaluationByOffice({
      application: this.applicationId,
      limit: 1
    });
    this.evaluation = results[0];
  }

  async fetchApplication() {
    this.application = await applicationService.fetchApplication(
      this.applicationId
    );
  }

  async fetchMessage() {
    const { count } = await messageService.fetchMessages({
      worker: authStore.user!.id,
      office: this.officeId!,
      // eslint-disable-next-line
      sender_type: "worker",
      limit: 1
    });
    this.isSentMessage = count > 0;
  }
}
</script>
